import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Bell, Heart, MessageCircle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NotificationBell() {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const pollRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(data.notifications);
      setUnreadCount(data.unread_count);
    } catch (e) {
      // silently fail
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
    pollRef.current = setInterval(fetchNotifications, 10000);
    return () => clearInterval(pollRef.current);
  }, [fetchNotifications]);

  const markRead = async () => {
    try {
      await axios.post(`${API}/notifications/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    } catch (e) {
      // silently fail
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (!showDropdown && unreadCount > 0) {
      markRead();
    }
  };

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Show browser notification for new unread
  const prevUnreadRef = useRef(0);
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current && 'Notification' in window && Notification.permission === 'granted') {
      const latest = notifications.find(n => !n.read);
      if (latest) {
        new Notification('LocalFlame', {
          body: latest.message,
          icon: '/favicon.ico'
        });
      }
    }
    prevUnreadRef.current = unreadCount;
  }, [unreadCount, notifications]);

  const getIcon = (type) => {
    switch (type) {
      case 'match': return <Heart className="w-4 h-4 text-rose-500 fill-rose-500" />;
      case 'message': return <MessageCircle className="w-4 h-4 text-blue-400" />;
      default: return <Bell className="w-4 h-4 text-zinc-400" />;
    }
  };

  return (
    <div className="relative">
      <button
        data-testid="notification-bell"
        onClick={toggleDropdown}
        className="relative w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-600 text-white text-xs flex items-center justify-center font-bold">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowDropdown(false)} />
          <div className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto z-50 bg-[#111114] rounded-2xl border border-zinc-800 shadow-2xl">
            <div className="flex items-center justify-between p-4 border-b border-zinc-800">
              <h3 className="font-outfit font-semibold text-white text-sm">Notifications</h3>
              <button onClick={() => setShowDropdown(false)} className="text-zinc-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {notifications.length === 0 ? (
              <div className="p-6 text-center text-zinc-500 text-sm">
                No notifications yet
              </div>
            ) : (
              <div className="divide-y divide-zinc-800/50">
                {notifications.slice(0, 20).map((n, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 p-3 ${!n.read ? 'bg-rose-500/5' : ''}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                      {getIcon(n.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-zinc-300 text-sm">{n.message}</p>
                      <p className="text-zinc-600 text-xs mt-0.5">
                        {new Date(n.created_at).toLocaleDateString()} {new Date(n.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    {!n.read && (
                      <div className="w-2 h-2 rounded-full bg-rose-500 flex-shrink-0 mt-2" />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
